import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface GrowthSeoProfile {
  id: string;
  targetType: string;
  targetRefId: string | null;
  title: string;
  description: string;
  keywordsJson: string;
  metaJson: string;
  schemaOrgJson: string;
  status: string;
  lastAuditAtUtc: string | null;
  score: number | null;
  recommendationJson: string | null;
}

export interface GrowthSeoAuditRun {
  id: string;
  seoProfileId: string;
  providerMode: string;
  score: number | null;
  findingsJson: string;
  recommendationsJson: string;
  completedAtUtc: string;
}

export interface GrowthGeoProfile {
  id: string;
  seoProfileId: string;
  entitySummary: string;
  factsJson: string;
  sourceRefsJson: string;
  lastScore: number | null;
}

export interface GrowthGeoScoreRun {
  id: string;
  geoProfileId: string;
  providerMode: string;
  score: number | null;
  signalsJson: string;
  completedAtUtc: string;
}

export interface GrowthAeoAnswer {
  id: string;
  question: string;
  answerMarkdown: string;
  locale: string;
  intentKey: string;
  citationRefsJson: string;
  approvedAtUtc: string | null;
}

export interface GrowthSxoExperiment {
  id: string;
  name: string;
  seoProfileId: string | null;
  status: string;
  variantAJson: string;
  variantBJson: string;
  resultJson: string | null;
}

export interface GrowthDiscoverySnapshot {
  id: string;
  windowKey: string;
  channelsJson: string;
  metricsJson: string;
  attributionJson: string;
}

@Injectable({ providedIn: 'root' })
export class GrowthSearchService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiBaseUrl}/api/v1/growth/search`;

  listSeoProfiles(): Observable<GrowthSeoProfile[]> {
    return this.http.get<GrowthSeoProfile[]>(`${this.base}/seo/profiles`);
  }

  upsertSeoProfile(payload: unknown): Observable<GrowthSeoProfile> {
    return this.http.post<GrowthSeoProfile>(`${this.base}/seo/profiles`, payload);
  }

  runSeoAudit(id: string, payload: unknown): Observable<GrowthSeoAuditRun> {
    return this.http.post<GrowthSeoAuditRun>(`${this.base}/seo/profiles/${id}/audit`, payload);
  }

  getStorefrontApplySuggestion(seoProfileId: string): Observable<StorefrontApplySuggestion> {
    return this.http.get<StorefrontApplySuggestion>(`${this.base}/seo/profiles/${seoProfileId}/storefront-apply-suggestion`);
  }

  listGeoProfiles(): Observable<GrowthGeoProfile[]> {
    return this.http.get<GrowthGeoProfile[]>(`${this.base}/geo/profiles`);
  }

  upsertGeoProfile(payload: unknown): Observable<GrowthGeoProfile> {
    return this.http.post<GrowthGeoProfile>(`${this.base}/geo/profiles`, payload);
  }

  runGeoScore(id: string, payload: unknown): Observable<GrowthGeoScoreRun> {
    return this.http.post<GrowthGeoScoreRun>(`${this.base}/geo/profiles/${id}/score`, payload);
  }

  listAeoAnswers(locale?: string): Observable<GrowthAeoAnswer[]> {
    let params = new HttpParams();
    if (locale)
      params = params.set('locale', locale);
    return this.http.get<GrowthAeoAnswer[]>(`${this.base}/aeo/answers`, { params });
  }

  upsertAeoAnswer(payload: unknown): Observable<GrowthAeoAnswer> {
    return this.http.post<GrowthAeoAnswer>(`${this.base}/aeo/answers`, payload);
  }

  approveAeoAnswer(id: string, approverUserId?: string | null): Observable<GrowthAeoAnswer> {
    return this.http.post<GrowthAeoAnswer>(`${this.base}/aeo/answers/${id}/approve`, { approverUserId: approverUserId ?? null });
  }

  listSxoExperiments(): Observable<GrowthSxoExperiment[]> {
    return this.http.get<GrowthSxoExperiment[]>(`${this.base}/sxo/experiments`);
  }

  upsertSxoExperiment(payload: unknown): Observable<GrowthSxoExperiment> {
    return this.http.post<GrowthSxoExperiment>(`${this.base}/sxo/experiments`, payload);
  }

  completeSxoExperiment(id: string, resultJson?: string | null): Observable<GrowthSxoExperiment> {
    return this.http.post<GrowthSxoExperiment>(`${this.base}/sxo/experiments/${id}/complete`, { resultJson: resultJson ?? null });
  }

  listDiscoverySnapshots(): Observable<GrowthDiscoverySnapshot[]> {
    return this.http.get<GrowthDiscoverySnapshot[]>(`${this.base}/discovery/snapshots`);
  }

  createDiscoverySnapshot(payload: unknown): Observable<GrowthDiscoverySnapshot> {
    return this.http.post<GrowthDiscoverySnapshot>(`${this.base}/discovery/snapshots`, payload);
  }
}

export interface StorefrontApplySuggestion {
  seoProfileId: string;
  targetType: string;
  contentAssetId?: string | null;
  offeringRefId?: string | null;
  requestPayloadJson: string;
  suggestedSeoOverrideJson: string;
}
